import { Dispatch, SetStateAction, useState } from 'react';

import { RegistrationType } from '@ndvr/customer-api';

import { ConfirmModal, ModalText } from 'widgets';

import {
    AccountFormValues,
    AccountRow,
    accountTitle,
    accountValue,
    clearedByRegistration,
    CORE_POSITIONS,
    countMissingAccountFields,
    FEATURE_LABELS,
    featuresFor,
    FUNDING_METHOD_LABELS,
    ownersBlocker,
    registrationOf,
    REGISTRATIONS,
    requiresTransferDetails,
    sameBeneficiaries,
    saveBlocker,
    toAccountFormValues,
    toAccountRequest,
    toDollars,
    toPortfolioBeneficiaries,
    TRANSFER_ASSETS,
    TRANSFER_TYPES,
    TransferValues,
    withRegistration,
} from './account-model';
import { BeneficiariesSection } from './BeneficiariesSection';
import {
    AutosaveNote,
    CardFooter,
    CollapsibleCard,
    FieldGroup,
    GroupTitle,
    needsAttention,
    NotSavedMark,
    NotSavedNote,
    ReadField,
    SelectField,
    StatusText,
    TextField,
    YesNoField,
} from './fields';
import { completion, joinNames, PersonKind } from './onboarding-model';
import { useDeleteOnboardingAccount, useSaveOnboardingAccount } from './onboarding-tab-queries';
import { OwnerPicker } from './OwnerPicker';
import { useAutosave } from './useAutosave';
import { OnboardingHousehold } from './useOnboardingHousehold';

const REGISTRATION_OPTIONS = REGISTRATIONS.map(({ value, label }) => ({ value, label }));

/** An account's expected value in dollars, read from a statement or heard on a call; empty while unknown. */
export const ExpectedValueField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <TextField
        required
        label="Expected value"
        value={value}
        onChange={onChange}
        placeholder="$ from the statement or call"
        error={value.trim() && !(Number(toDollars(value)) > 0) ? 'Enter a dollar amount greater than zero' : undefined}
    />
);

export const AccountCard = ({
    householdGUID,
    household,
    row,
    open,
    onToggle,
    addPerson,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
    row: AccountRow;
    open: boolean;
    onToggle: () => void;
    addPerson: (kind: PersonKind, onCreated: (personOrgGUID: string) => void) => void;
}) => {
    const { account } = row;
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState<RegistrationType>();
    const save = useSaveOnboardingAccount(householdGUID);
    const remove = useDeleteOnboardingAccount(householdGUID);
    const takesBeneficiariesOf = (form: AccountFormValues) => !!registrationOf(form.registrationType)?.beneficiaries;
    const { values, setValues, autosave } = useAutosave({
        stored: toAccountFormValues(row),
        check: (form) =>
            saveBlocker(
                form.registrationType,
                form.expectedValue,
                takesBeneficiariesOf(form) ? form.beneficiaries : undefined,
            ) ?? ownersBlocker(form.registrationType, form.owners),
        save: (form, saved) =>
            save.mutateAsync({
                accountGUID: account.accountGUID,
                // The check refuses an account without a registration type
                request: toAccountRequest({ ...form, registrationType: form.registrationType! }),
                beneficiaries:
                    takesBeneficiariesOf(form) && !sameBeneficiaries(form.beneficiaries, saved.beneficiaries)
                        ? toPortfolioBeneficiaries(form.beneficiaries)
                        : undefined,
            }),
    });
    const {
        registrationType,
        owners,
        expectedValue,
        contraFirm,
        contraAccountNumber,
        transfer,
        beneficiaries,
        features,
    } = values;
    /** A field's setter; it also takes an update of the field's current value, as the owner and beneficiary editors send. */
    const set =
        <K extends keyof AccountFormValues>(key: K): Dispatch<SetStateAction<AccountFormValues[K]>> =>
        (action) =>
            setValues((current) => ({
                ...current,
                [key]:
                    typeof action === 'function'
                        ? (action as (previous: AccountFormValues[K]) => AccountFormValues[K])(current[key])
                        : action,
            }));
    const setTransferValue =
        <K extends keyof TransferValues>(key: K) =>
        (value: string) =>
            setValues((current) => ({ ...current, transfer: { ...current.transfer, [key]: value } }));

    const registration = registrationOf(registrationType);
    const shape = registration?.owner ?? 'PERSON';
    const takesBeneficiaries = takesBeneficiariesOf(values);
    const missing = countMissingAccountFields(values);
    const transferRequired = requiresTransferDetails(values);
    const attention = needsAttention(autosave);

    /** A change that clears owners or answers is confirmed first: it saves right away, and picking back restores nothing. */
    const changeRegistration = (value: string) => {
        const next = value as RegistrationType;
        const cleared = clearedByRegistration(values, withRegistration(values, next));
        if (cleared.owners || cleared.features.length) setPendingRegistration(next);
        else setValues((current) => withRegistration(current, next));
    };
    const pendingCleared =
        pendingRegistration && clearedByRegistration(values, withRegistration(values, pendingRegistration));

    return (
        <CollapsibleCard
            id={`account-${account.accountGUID}`}
            open={open}
            onToggle={onToggle}
            bodyProps={autosave.bodyProps}
            header={
                <>
                    <span className="min-w-0 flex-1 truncate font-medium">{accountTitle(account)}</span>
                    <span className="min-w-24 text-right whitespace-nowrap">{accountValue(account)}</span>
                    {attention && <NotSavedMark />}
                    <StatusText {...completion(missing)} />
                </>
            }
        >
            <div className="border-cocoa-100 flex flex-col gap-3 border-b py-[18px]">
                <GroupTitle>Registration</GroupTitle>
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-start gap-x-5 gap-y-4">
                    <SelectField
                        required
                        label="Registration type"
                        value={registrationType ?? ''}
                        onChange={changeRegistration}
                        options={REGISTRATION_OPTIONS}
                        placeholder="Select…"
                    />
                    {/* Set by the backend from the registration and owners */}
                    <ReadField label="Account title" value={account.title || '—'} />
                </div>
                <span className="mt-1.5 text-[13px] font-medium">
                    {shape === 'ENTITY' ? 'Owner (entity)' : 'Owners'}
                </span>
                <OwnerPicker
                    shape={shape}
                    entityTypes={registration?.entityTypes}
                    owners={owners}
                    onChange={set('owners')}
                    household={household}
                    addPerson={addPerson}
                />
                {new Set(owners.filter(Boolean)).size < (shape === 'JOINT' ? 2 : 1) && (
                    <span className="text-gold-600 text-xs font-medium">
                        Required{shape === 'JOINT' ? ' — joint accounts need two owners' : ''}
                    </span>
                )}
            </div>

            <FieldGroup title={<>Funding</>} columns={4}>
                <TextField
                    required={transferRequired}
                    label="Contra firm"
                    value={contraFirm}
                    onChange={set('contraFirm')}
                    placeholder="e.g. Morgan Stanley"
                />
                <TextField
                    required={transferRequired}
                    label="Contra account"
                    value={contraAccountNumber}
                    onChange={set('contraAccountNumber')}
                />
                <SelectField
                    required={transferRequired}
                    label="Transfer"
                    value={transfer.transferType}
                    onChange={setTransferValue('transferType')}
                    options={TRANSFER_TYPES}
                />
                <SelectField
                    required={transferRequired}
                    label="Assets"
                    value={transfer.transferAssets}
                    onChange={setTransferValue('transferAssets')}
                    options={TRANSFER_ASSETS}
                />
                <TextField
                    required={registrationType === RegistrationType.ROLLOVER_IRA}
                    label="Rolling over from"
                    value={transfer.rolloverFrom}
                    onChange={setTransferValue('rolloverFrom')}
                    placeholder="Plan or custodian name"
                />
                {/* Set by the backend: a transfer, or a rollover into a Rollover IRA, when the money comes from another firm */}
                <ReadField
                    label="Method"
                    value={account.fundingMethod ? FUNDING_METHOD_LABELS[account.fundingMethod] : '—'}
                />
                <ExpectedValueField value={expectedValue} onChange={set('expectedValue')} />
            </FieldGroup>

            {takesBeneficiaries && (
                <BeneficiariesSection
                    household={household}
                    account={account}
                    owners={owners}
                    title={registration?.transferOnDeath ? 'Beneficiaries (TOD)' : 'Beneficiaries'}
                    beneficiaries={beneficiaries}
                    onChange={set('beneficiaries')}
                    addPerson={addPerson}
                />
            )}

            {!!featuresFor(registrationType).length && (
                <FieldGroup title="Features">
                    {featuresFor(registrationType).map((feature) => (
                        <YesNoField
                            required
                            key={feature}
                            label={FEATURE_LABELS[feature]}
                            value={features[feature]}
                            onChange={(value) =>
                                setValues((current) => ({
                                    ...current,
                                    features: { ...current.features, [feature]: value },
                                }))
                            }
                        />
                    ))}
                </FieldGroup>
            )}

            <FieldGroup title="Cash">
                <SelectField
                    required
                    label="Core position"
                    value={transfer.corePosition}
                    onChange={setTransferValue('corePosition')}
                    options={CORE_POSITIONS}
                />
            </FieldGroup>

            <div className="border-cocoa-100 flex flex-col gap-3 border-b py-[18px]">
                <GroupTitle action={<NotSavedNote>Set once Fidelity opens the account</NotSavedNote>}>
                    Fidelity account number
                </GroupTitle>
                {/* TODO(backend): custody accounts link only to REAL accounts, not onboarding ones */}
                <div className="max-w-80">
                    <TextField value="" onChange={() => {}} disabled />
                </div>
            </div>

            <CardFooter floating={attention}>
                <AutosaveNote autosave={autosave} />
                <span className="flex-1" />
                <button
                    type="button"
                    disabled={remove.isPending}
                    className="text-cocoa-600 cursor-pointer text-[13px] underline underline-offset-[3px]"
                    onClick={() => setConfirmRemove(true)}
                >
                    Delete account
                </button>
            </CardFooter>
            {pendingRegistration && pendingCleared && (
                <ConfirmModal
                    modalTitle={`Change the registration to ${registrationOf(pendingRegistration)?.label}`}
                    onConfirm={() => setValues((current) => withRegistration(current, pendingRegistration))}
                    buttonProps={{ children: 'Change registration' }}
                    onClose={() => setPendingRegistration(undefined)}
                >
                    <ModalText>
                        This clears{' '}
                        {joinNames([
                            ...(pendingCleared.owners ? ['the owners'] : []),
                            ...(pendingCleared.features.length
                                ? [
                                      `the ${joinNames(pendingCleared.features)} answer${
                                          pendingCleared.features.length > 1 ? 's' : ''
                                      }`,
                                  ]
                                : []),
                        ])}
                        , and the change is saved right away.
                    </ModalText>
                </ConfirmModal>
            )}
            {confirmRemove && (
                <ConfirmModal
                    modalTitle={`Delete account ${accountTitle(account)}`}
                    onConfirm={() => remove.mutate(account.accountGUID)}
                    buttonProps={{ buttonType: 'destructive', children: 'Delete account' }}
                    onClose={() => setConfirmRemove(false)}
                >
                    <ModalText>
                        The account and its beneficiary assignments will be deleted. The people’s records will be kept.
                    </ModalText>
                </ConfirmModal>
            )}
        </CollapsibleCard>
    );
};
